/** XeAgents.ai**/
/**
 * A whole flow, read as a script.
 *
 * A flow is stored as a graph, and a graph is the wrong shape for the question
 * people actually ask of it: "what does this agent SAY?". Answering that today
 * means opening fifty nodes one at a time, which is how a prompt that told an
 * agent to narrate its own routing survived long enough to reach a live call.
 *
 * So this walks the graph once and returns it as ordered steps: every line the
 * agent can speak, every branch it can take and what takes it there, every
 * variable it needs, and every way the call can end.
 *
 * It is deliberately READ-ONLY and lossless in one direction only. A step knows
 * which node it came from, so a reader can jump to it and edit there. Nothing
 * here writes back: most nodes carry more than words - conditions, variables,
 * tool wiring - so a script can describe a flow but cannot reconstruct one, and
 * pretending otherwise would lose the parts it does not show.
 *
 * Lives in shared/ because the builder renders it from unsaved on-screen state
 * while the server derives it from what is stored; two implementations of "what
 * order does a call go in" would drift, and the order is the whole point.
 */

/** One thing the agent says, and why it says it here. */
export interface ScriptSpokenLine {
  text: string;
  /** "says", "asks", "confirms"… - how this line functions in the step. */
  role: string;
}

/** One way out of a step. */
export interface ScriptBranch {
  /** The condition in words, as a reader would say it aloud. */
  when: string;
  toNodeId: string;
  toLabel: string;
  /** 1-based step number of the target, absent if the target is missing. */
  toStep?: number;
  /** Things a caller might say that take this branch. */
  examples: string[];
}

export interface ScriptStep {
  /** 1-based, in the order a call reaches them. */
  step: number;
  nodeId: string;
  label: string;
  /** The node's type, e.g. "message", "multiCondition". */
  kind: string;
  /** One sentence on what this step is for. */
  summary: string;
  says: ScriptSpokenLine[];
  /** Variables this step captures from the caller. */
  collects: string[];
  branches: ScriptBranch[];
  /** Nothing points at this node - it can never be reached on a call. */
  unreachable: boolean;
  /** The outcome recorded if the call ends here. */
  outcome?: string;
}

export interface FlowScript {
  steps: ScriptStep[];
  /** Every {{placeholder}} the script speaks, deduplicated, in first-spoken order. */
  variables: string[];
  /** Node ids no edge and no intent reaches. */
  orphans: string[];
  /** Outcomes the call can be recorded as. */
  outcomes: string[];
  /** Spoken lines across the whole flow - what "end to end" amounts to. */
  totalLines: number;
}

type AnyNode = Record<string, any>;
type AnyEdge = Record<string, any>;

/** What each node type is for, in one sentence a non-engineer can read. */
const KIND_SUMMARY: Record<string, string> = {
  start: 'Where the call begins.',
  message: 'The agent speaks, then listens.',
  multiCondition: 'Silent. Works out what the caller meant and picks a branch.',
  condition: 'Silent. Splits the call in two on a yes/no.',
  form: 'Asks a set of questions and records the answers.',
  question: 'Asks one question and records the answer.',
  appointment: 'Offers times and books one.',
  transfer: 'Hands the call to a person.',
  voicemail: 'Leaves a message on an answering machine.',
  aiDisclosure: 'Confirms to the caller that they are speaking to an AI.',
  referralCapture: 'Asks who the right person would be, and how to reach them.',
  callback: 'Agrees a better time to call back.',
  doNotCall: 'Records that this number must not be called again.',
  end: 'The call ends here.',
};

/**
 * Every spoken field, in the order a caller hears it, with the role each plays.
 *
 * Ordered rather than alphabetical: a node that greets, asks and then confirms
 * has to read in that sequence or the script misrepresents the call.
 */
const SPOKEN_FIELDS: Array<{ key: string; role: string }> = [
  { key: 'disclosureMessage', role: 'discloses' },
  { key: 'message', role: 'says' },
  { key: 'question', role: 'asks' },
  { key: 'namePrompt', role: 'asks' },
  { key: 'phonePrompt', role: 'asks' },
  { key: 'confirmMessage', role: 'confirms' },
  { key: 'successMessage', role: 'confirms' },
  { key: 'endMessage', role: 'signs off' },
];

/** Config keys that name a variable this node writes. */
const VARIABLE_FIELDS = ['variableName', 'variable', 'nameVariable', 'phoneVariable'];

const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;

function nodeKind(node: AnyNode): string {
  return String(node?.data?.type || node?.type || 'step');
}

function nodeLabel(node: AnyNode): string {
  return String(node?.data?.label || node?.id || 'Step');
}

/** Everything this node says, in the order it is said. */
function spokenLines(node: AnyNode): ScriptSpokenLine[] {
  const config = node?.data?.config;
  if (!config) return [];
  const lines: ScriptSpokenLine[] = [];

  for (const { key, role } of SPOKEN_FIELDS) {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) {
      lines.push({ text: value.trim(), role });
    }
  }

  // A form's questions are spoken after its intro message, one per field.
  for (const field of config.fields ?? []) {
    if (typeof field?.question === 'string' && field.question.trim()) {
      lines.push({ text: field.question.trim(), role: 'asks' });
    }
  }

  return lines;
}

/** The variables this step records from the caller. */
function collectedVariables(node: AnyNode): string[] {
  const config = node?.data?.config;
  if (!config) return [];
  const names: string[] = [];

  for (const key of VARIABLE_FIELDS) {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) names.push(value.trim());
  }
  for (const field of config.fields ?? []) {
    if (typeof field?.name === 'string' && field.name.trim()) names.push(field.name.trim());
  }

  return [...new Set(names)];
}

/**
 * Where a call can go from here, and what sends it each way.
 *
 * A router's branches live on its intents, not only on its edges: the intent
 * carries the description and the caller phrases, the edge carries the wiring,
 * and they are joined by the intent name in `sourceHandle`. Reading only edges
 * gives "5 branches, no idea what picks them"; reading only intents misses every
 * plain node. So both, intents first, with edges filling in whatever they did
 * not already explain.
 */
function branchesFrom(
  node: AnyNode,
  edges: AnyEdge[],
  byId: Map<string, AnyNode>,
): ScriptBranch[] {
  const config = node?.data?.config ?? {};
  const outgoing = (edges || []).filter(edge => edge?.source === node.id);
  const branches: ScriptBranch[] = [];
  const claimed = new Set<string>();

  for (const intent of config.intents ?? []) {
    const target = intent?.targetNode
      ?? outgoing.find(edge => edge?.sourceHandle === intent?.name)?.target;
    if (!target) continue;
    claimed.add(String(target));
    branches.push({
      when: String(intent?.description || intent?.name || 'a matching reply'),
      toNodeId: String(target),
      toLabel: byId.has(String(target)) ? nodeLabel(byId.get(String(target))!) : String(target),
      examples: (intent?.examples ?? []).filter((e: any) => typeof e === 'string'),
    });
  }

  for (const edge of outgoing) {
    const target = String(edge?.target ?? '');
    if (!target || claimed.has(target)) continue;
    claimed.add(target);
    // sourceHandle is "true"/"false" on a condition node and an intent name on a
    // router; anything else is an unconditional hop.
    const handle = edge?.sourceHandle;
    const when = handle === 'true' ? 'if yes'
      : handle === 'false' ? 'if no'
      : typeof handle === 'string' && handle ? humanise(handle)
      : typeof edge?.label === 'string' && edge.label.trim() ? edge.label.trim()
      : 'always';
    branches.push({
      when,
      toNodeId: target,
      toLabel: byId.has(target) ? nodeLabel(byId.get(target)!) : target,
      examples: [],
    });
  }

  return branches;
}

/** "right_person" -> "right person", so a handle reads as a condition. */
function humanise(value: string): string {
  return value.replace(/[_-]+/g, ' ').trim();
}

/**
 * Nodes in the order a call reaches them: breadth-first from the start node.
 *
 * Breadth-first rather than depth-first so the main path reads top to bottom
 * before the branches do - depth-first would open with the greeting and then
 * disappear down the voicemail path for thirty steps.
 */
function callOrder(nodes: AnyNode[], edges: AnyEdge[]): { ordered: AnyNode[]; reached: Set<string> } {
  const byId = new Map<string, AnyNode>();
  for (const node of nodes || []) byId.set(String(node.id), node);

  const next = new Map<string, string[]>();
  const push = (from: string, to: string) => {
    if (!next.has(from)) next.set(from, []);
    if (!next.get(from)!.includes(to)) next.get(from)!.push(to);
  };
  for (const edge of edges || []) {
    if (edge?.source && edge?.target) push(String(edge.source), String(edge.target));
  }
  for (const node of nodes || []) {
    for (const intent of node?.data?.config?.intents ?? []) {
      if (intent?.targetNode) push(String(node.id), String(intent.targetNode));
    }
  }

  const start = (nodes || []).find(n => nodeKind(n) === 'start');
  const ordered: AnyNode[] = [];
  const reached = new Set<string>();
  const queue: string[] = start ? [String(start.id)] : (nodes || []).slice(0, 1).map(n => String(n.id));
  if (queue[0]) reached.add(queue[0]);

  while (queue.length) {
    const id = queue.shift()!;
    const node = byId.get(id);
    if (node) ordered.push(node);
    for (const target of next.get(id) || []) {
      if (reached.has(target)) continue;
      reached.add(target);
      queue.push(target);
    }
  }

  // Unreachable nodes are still part of the flow someone is looking at, and
  // being told they can never run is more useful than their silent absence.
  for (const node of nodes || []) {
    if (!reached.has(String(node.id))) ordered.push(node);
  }

  return { ordered, reached };
}

/**
 * Read a whole flow as a script.
 *
 * @param nodes React Flow nodes as stored on the flow
 * @param edges React Flow edges as stored on the flow
 */
export function buildFlowScript(nodes: AnyNode[], edges: AnyEdge[]): FlowScript {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeEdges = Array.isArray(edges) ? edges : [];

  const byId = new Map<string, AnyNode>();
  for (const node of safeNodes) byId.set(String(node.id), node);

  const { ordered, reached } = callOrder(safeNodes, safeEdges);
  const stepOf = new Map<string, number>();
  ordered.forEach((node, index) => stepOf.set(String(node.id), index + 1));

  const variables: string[] = [];
  const outcomes: string[] = [];
  const orphans: string[] = [];
  let totalLines = 0;

  const steps: ScriptStep[] = ordered.map((node, index) => {
    const kind = nodeKind(node);
    const says = spokenLines(node);
    totalLines += says.length;

    for (const line of says) {
      PLACEHOLDER.lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = PLACEHOLDER.exec(line.text)) !== null) {
        if (!variables.includes(match[1])) variables.push(match[1]);
      }
    }

    const outcome = typeof node?.data?.config?.outcome === 'string'
      ? node.data.config.outcome
      : kind === 'end' ? 'completed' : undefined;
    if (outcome && !outcomes.includes(outcome)) outcomes.push(outcome);

    const unreachable = !reached.has(String(node.id));
    if (unreachable) orphans.push(String(node.id));

    return {
      step: index + 1,
      nodeId: String(node.id),
      label: nodeLabel(node),
      kind,
      summary: KIND_SUMMARY[kind] ?? 'Runs this step.',
      says,
      collects: collectedVariables(node),
      branches: branchesFrom(node, safeEdges, byId).map(branch => ({
        ...branch,
        toStep: stepOf.get(branch.toNodeId),
      })),
      unreachable,
      outcome,
    };
  });

  return { steps, variables, orphans, outcomes, totalLines };
}
